import type { ReactNode } from 'react';
import * as RP from '@radix-ui/react-popover';
import { cn } from './cn';

export type Placement =
  | 'top' | 'bottom' | 'left' | 'right'
  | 'topLeft' | 'topRight' | 'bottomLeft' | 'bottomRight'
  | 'leftTop' | 'leftBottom' | 'rightTop' | 'rightBottom';

export function sideFor(p?: Placement): 'top' | 'bottom' | 'left' | 'right' {
  if (!p) return 'bottom';
  if (p.startsWith('top')) return 'top';
  if (p.startsWith('bottom')) return 'bottom';
  if (p.startsWith('left')) return 'left';
  return 'right';
}
export function alignFor(p?: Placement): 'start' | 'center' | 'end' {
  if (!p) return 'center';
  if (p.endsWith('Left') || p.endsWith('Top')) return 'start';
  if (p.endsWith('Right') || p.endsWith('Bottom')) return 'end';
  return 'center';
}

/** Ant Design `Popover` replacement (click-triggered), built on Radix Popover. */
export function Popover({
  content, title, placement = 'bottom', open, onOpenChange, children, overlayClassName,
}: {
  content: ReactNode;
  title?: ReactNode;
  placement?: Placement;
  trigger?: 'click' | 'hover';
  open?: boolean;
  onOpenChange?: (o: boolean) => void;
  children: ReactNode;
  overlayClassName?: string;
}) {
  return (
    <RP.Root open={open} onOpenChange={onOpenChange}>
      <RP.Trigger asChild>{children}</RP.Trigger>
      <RP.Portal>
        <RP.Content
          side={sideFor(placement)}
          align={alignFor(placement)}
          sideOffset={6}
          // `max-w-xs` is the default cap for TEXT popovers. cn() is a plain joiner (no tailwind-merge),
          // so a caller's own `max-w-…` would lose to it on CSS order — drop the default whenever the
          // caller declares one, else a wider body (a form, a picker) spills outside the card.
          className={cn(
            'z-[var(--z-popover)] rounded-card border border-line bg-surface p-3 shadow-pop animate-pop-in',
            !/(^|\s)max-w-/.test(overlayClassName ?? '') && 'max-w-xs',
            overlayClassName,
          )}
        >
          {title != null && <div className="mb-1.5 type-body-strong text-content">{title}</div>}
          <div className="text-sm text-muted">{content}</div>
        </RP.Content>
      </RP.Portal>
    </RP.Root>
  );
}
